import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flame } from 'lucide-react';
import { navigationFromState } from '@/utils/navigateBack';
import { playClickSound } from '@/utils/soundUtils';
import { Button, Popover, focusRing, cx } from '@/components/ui';
import CampaignOfferMarks from './CampaignOfferMarks';
import { offerUntil, offerWhat } from './campaignOfferCopy';
import { useCampaignBonusOffers } from './CampaignBonusOffersContext';

/** Amber chip in the header. Opens the live offers; not a plain notice bar. */
export default function CampaignBonusStrip() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { offers } = useCampaignBonusOffers();
  if (offers.length === 0) return null;

  const label = t('campaignBonus.hotOffer', 'Hot offer');

  const goAddFunds = (close: () => void) => {
    playClickSound();
    close();
    if (location.pathname !== '/money/add') {
      navigate('/money/add', navigationFromState(location.pathname));
    }
  };

  return (
    <Popover
      aria-label={label}
      align="end"
      width={320}
      scrollable
      className="font-ui text-small"
      trigger={(trigger) => (
        <button
          type="button"
          ref={trigger.ref}
          aria-haspopup={trigger['aria-haspopup']}
          aria-expanded={trigger['aria-expanded']}
          aria-controls={trigger['aria-controls']}
          aria-label={label}
          data-testid="campaign-bonus-strip"
          onClick={() => {
            playClickSound();
            trigger.onClick();
          }}
          className={cx(
            'inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-button border border-warning/40 bg-warning/15 px-4.5',
            'font-ui text-small font-semibold whitespace-nowrap text-warning transition-colors duration-(--k-dur-fast) hover:bg-warning/25',
            focusRing
          )}
        >
          <Flame aria-hidden className="size-4 shrink-0" />
          {label}
        </button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-2 p-3" data-testid="campaign-bonus-offers-panel">
          <div className="flex items-center gap-2 px-0.5 text-warning">
            <Flame aria-hidden className="size-4 shrink-0" />
            <p className="m-0 font-semibold leading-snug">{label}</p>
          </div>
          {offers.map((offer) => (
            <div
              key={offer.id}
              className={cx(
                'rounded-control border px-3 py-2.5',
                offer.used ? 'border-edge-subtle bg-inset' : 'border-warning/32 bg-warning/12'
              )}
            >
              <p className="m-0 mb-0.5 font-semibold leading-snug text-primary">{offer.playerMessage}</p>
              <p className={cx('m-0 mb-1 leading-snug', offer.used ? 'text-secondary' : 'text-warning')}>
                {offerWhat(offer)}
              </p>
              {offerUntil(offer.endsAt) ? (
                <p className="m-0 text-tertiary">{offerUntil(offer.endsAt)}</p>
              ) : null}
              <CampaignOfferMarks offer={offer} />
            </div>
          ))}
          <Button variant="primary" fullWidth onClick={() => goAddFunds(close)}>
            {t('header.addMoney', 'Add money')}
          </Button>
        </div>
      )}
    </Popover>
  );
}
